import type { logger } from '@sentry/nextjs';

type Attrs = Record<string, unknown>;

export type NamedLogEntry = { name: string } & Record<string, unknown>;

export type NamedLogMethod = {
  (entry: NamedLogEntry, msg?: string, ...args: unknown[]): void;
  (msg: string, ...args: unknown[]): void;
};

export interface NamedLogger {
  trace: NamedLogMethod;
  debug: NamedLogMethod;
  info: NamedLogMethod;
  warn: NamedLogMethod;
  error: NamedLogMethod;
  fatal: NamedLogMethod;
}

const FORWARD_TO_SENTRY = true;
// Mirror logs to the browser/server console in ALL environments (incl. production) — the deployed
// server runs NODE_ENV=production, and we want plog output visible in the browser console there.
// Restore dev-only console output with `MIRROR_TO_CONSOLE = process.env.NODE_ENV !== 'production'`.
const MIRROR_TO_CONSOLE = true;
const DISABLED = (globalThis as { __DISABLED_LOGS__?: unknown }).__DISABLED_LOGS__ === true;

type SentryLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

function redact(attributes: Attrs): Attrs {
  const serialized = JSON.stringify(attributes, (key, rawValue) => {
    if (typeof rawValue === 'string') {
      if (/Bearer\s+\S+/i.test(rawValue)) {
        return rawValue.replace(/(Bearer)\s+\S+/i, '$1 <redacted>');
      }
      if (/api[_-]?key|token|authorization|cookie/i.test(key)) {
        return '<redacted>';
      }
    }
    return rawValue;
  });

  return JSON.parse(serialized);
}

let sentryLogger: typeof logger | null = null;

async function forwardToSentry(level: SentryLevel, name: string, attrs: Attrs) {
  if (!FORWARD_TO_SENTRY || DISABLED) return;

  if (!sentryLogger) {
    const { logger } = await import('@sentry/nextjs');
    sentryLogger = logger;
  }

  sentryLogger[level](name, attrs);
}

const LEVEL_ORDER: SentryLevel[] = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'];
const configuredLevel = (process.env.NEXT_PUBLIC_LOG_LEVEL ?? 'info') as SentryLevel;
const minLevel = LEVEL_ORDER.indexOf(configuredLevel);

const consoleMethods: Record<SentryLevel, 'log' | 'warn' | 'error'> = {
  trace: 'log',
  debug: 'log',
  info: 'log',
  warn: 'warn',
  error: 'error',
  fatal: 'error',
};

function createLogMethod(level: SentryLevel): NamedLogMethod {
  const levelIndex = LEVEL_ORDER.indexOf(level);

  return ((...args: unknown[]) => {
    if (DISABLED || levelIndex < minLevel) return;

    const first = args[0];
    let label: string;
    let message: string;
    let attrs: Attrs;

    if (typeof first === 'object' && first !== null && 'name' in first) {
      const { name, ...rest } = first as NamedLogEntry;
      label = name;
      message = typeof args[1] === 'string' ? (args[1] as string) : '';
      attrs = redact(rest);
    } else {
      label = 'app';
      message = typeof first === 'string' ? first : '';
      const extra = args[1];
      attrs = extra && typeof extra === 'object' ? redact(extra as Attrs) : {};
    }

    if (MIRROR_TO_CONSOLE) {
      const method = consoleMethods[level];
      // Log a flat string, NOT an object reference. A logged object ref is evaluated lazily by
      // devtools, so it becomes uninspectable (or mutated/dead) once the page redirects — which the
      // fanza gate does constantly. A stringified snapshot is captured at log time and survives.
      const hasAttrs = Object.keys(attrs).length > 0;
      const line = hasAttrs ? `[${label}] ${message} ${JSON.stringify(attrs)}` : `[${label}] ${message}`;
      console[method](line);
    }

    void forwardToSentry(level, label, attrs);
  }) as NamedLogMethod;
}

export function createLogger(): NamedLogger {
  return {
    trace: createLogMethod('trace'),
    debug: createLogMethod('debug'),
    info: createLogMethod('info'),
    warn: createLogMethod('warn'),
    error: createLogMethod('error'),
    fatal: createLogMethod('fatal'),
  };
}
